import { FC, useContext, useState } from 'react';
import { motion } from 'motion/react';
import { TypeBodyLarge } from 'components/library/Typography';
import styled, { css } from 'styled-components';

import Button from 'components/library/Button';
import { validateEmail } from 'lib/utils/validate-email';
import { UserContext } from 'context-providers/user-provider';
import LoginLocale from 'locale/en/login.json';
import { apiLoginRoutingGet, apiShopifyInitiateAuth } from 'lib/api';
import { setToSessionStorage } from 'lib/utils/set-to-session-storage';
import Router, { useRouter } from 'next/router';

interface LoginDropdownPercentRolloutProps {
  id: string;
  variants: any;
}

const LoginWrapper = styled.div`
  padding: var(--spacing-8);
`;

const LoginContainer = styled.div`
  --color-text: var(--fill-bright);
  margin-bottom: var(--spacing-16);

  > * + * {
    margin-top: var(--spacing-4);
  }
`;

const LoginTitle = styled(TypeBodyLarge)`
  && {
    font-weight: 500;
  }
`;

const LoginSubtitle = styled.p`
  color: var(--fill-primary);
  font-size: var(--font-size-350);
  font-weight: 350;
  line-height: 1.4;
  letter-spacing: -0.01em;
`;

const LoginForm = styled.form`
  & > * + * {
    margin-top: var(--spacing-16);
  }
`;

const LoginType = css`
  color: var(--fill-bright);
  font-size: var(--font-size-350);
  font-weight: 350;
`;

const LoginFormFieldset = styled.fieldset`
  & > * + * {
    margin-top: var(--spacing-16);
  }
  border: 0;
  padding: 0;
`;

const LoginFormInput = styled.input`
  && {
    ${LoginType}
    background-color: transparent;
    border: 1px solid var(--outline-light-20);
    border-radius: var(--radius-sm);
    outline: 0;
    padding: 14px 20px;
    height: 48px;

    &::placeholder {
      ${LoginType}
    }

    &:focus {
      border-color: var(--fill-primary);
    }
  }
`;

const LoginFormError = styled.div`
  color: var(--fill-primary);
  background: var(--text-error);
  border-radius: var(--bdrs-s);
  font-size: var(--font-size-350);
  font-weight: 350;
  line-height: 140%;
  padding: 1em 1.45em;
`;

const LoginFormSubmitRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const LoginButton = styled.div`
  place-self: center end;
`;

const LoginDropdownPercentRollout: FC<LoginDropdownPercentRolloutProps> = ({
  id,
  variants,
}) => {
  const {
    authenticationFormErrors,
    setAuthenticationFormErrors,
    clearAuthFormErrors,
  } = useContext(UserContext);
  const router = useRouter();

  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');

  const userEmailHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearAuthFormErrors();
    setEmail(e.target.value);
  };

  const loginRouting = async (email: string) => {
    try {
      const response = await apiLoginRoutingGet(email);

      if (response.status !== 200) {
        setAuthenticationFormErrors({ emailError: LoginLocale.error_generic });
        setIsLoading(false);
        return;
      }

      const { gateway } = response.data;

      if (gateway === 'shopify') {
        const redirect =
          typeof router.query.redirect === 'string'
            ? router.query.redirect
            : undefined;
        const shopifyResponse = await apiShopifyInitiateAuth(redirect);
        // This must be a full-page redirect (not fetch) to avoid CORS issues
        if (
          shopifyResponse.status === 200 &&
          shopifyResponse.data?.authorizationUrl
        ) {
          setToSessionStorage('pending_shopify_login', 'true');
          window.location.href = shopifyResponse.data.authorizationUrl;
        } else {
          setAuthenticationFormErrors({
            emailError: LoginLocale.error_generic,
          });
          setIsLoading(false);
        }
      } else {
        // standard login (recurly gateway)
        Router.push(
          '/login?email=' + encodeURIComponent(email) + '&gateway=recurly',
        );
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error determining login gateway:', error);
      setAuthenticationFormErrors({ emailError: LoginLocale.error_generic });
      setIsLoading(false);
    }
  };

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsLoading(true);
    const emailIsInValid = !email || !validateEmail(email);

    if (emailIsInValid) {
      const errors = { emailError: '' };
      if (emailIsInValid) {
        errors.emailError = LoginLocale.error_email;
      }

      setAuthenticationFormErrors(errors);
      setIsLoading(false);
    } else {
      clearAuthFormErrors();
      await loginRouting(email);
    }
  }

  return (
    <motion.div
      key={id}
      variants={variants}
      initial='initial'
      animate='visible'
      exit='exit'
    >
      <LoginWrapper>
        <LoginContainer>
          <LoginTitle as='h3' color='inverse-primary'>
            Welcome back
          </LoginTitle>

          <LoginSubtitle>
            Enter your email to access your account.
          </LoginSubtitle>
        </LoginContainer>
        <LoginForm onSubmit={onSubmit}>
          <LoginFormFieldset>
            <LoginFormInput
              type='email'
              placeholder='Email address'
              value={email}
              onChange={userEmailHandler}
            />
          </LoginFormFieldset>
          {authenticationFormErrors?.emailError && (
            <LoginFormError>
              {authenticationFormErrors.emailError}
            </LoginFormError>
          )}
          <LoginFormSubmitRow>
            <LoginButton>
              <Button
                color='snowwhite'
                size='medium'
                type='submit'
                label='Continue'
                disabled={isLoading}
              />
            </LoginButton>
          </LoginFormSubmitRow>
        </LoginForm>
      </LoginWrapper>
    </motion.div>
  );
};

export default LoginDropdownPercentRollout;
